import { makeAutoObservable } from 'mobx';

import ApiService, { FetchStatus } from '../services/ApiService';
import ErrorUtil from '../utils/ErrorUtil';

import { IPrepaidInfo, PrepaidBrand } from './models/IPrepaidInfo';
import { IRouteStore } from './RouteStore';

class MobileNumberStore {
  public mobileNumber = '';
  public prepaidInfo: IPrepaidInfo = null;
  public prepaidInfoStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  public validNumber = true;
  public validationDone = false;
  public isRefillable: boolean = true;

  constructor(
    private routeStore: IRouteStore,
    private apiService: ApiService,
  ) {
    makeAutoObservable<MobileNumberStore, 'routeStore' | 'apiService'>(this, { routeStore: false, apiService: false }, { autoBind: true });
    this.handleQueryParamMsisdn();
  }

  public setMobileNumber(mobileNumber: string): void {
    this.mobileNumber = mobileNumber.trim();
    this.setValidationDone(false);
  }

  public async validateMobileNumber(): Promise<boolean> {
    let valid = false;
    this.isRefillable = true;

    if (this.mobileNumber.length >= 6) {
      try {
        this.prepaidInfoStatus = FetchStatus.FETCHING;
        const prepaidInfo = await this.apiService.getPrepaidInfo(this.mobileNumber);

        if (prepaidInfo && prepaidInfo.brand && prepaidInfo.brand !== PrepaidBrand.UNKNOWN) {
          if (prepaidInfo.refillable === false) {
            this.isRefillable = false;
            valid = false;
          } else {
            this.setPrepaidInfo(prepaidInfo);
            valid = true;
          }
        }
        this.prepaidInfoStatus = FetchStatus.FETCHED;
      } catch (error: any) {
        ErrorUtil.registerError(error);
        valid = false;
        this.prepaidInfoStatus = FetchStatus.ERROR;
      }
    }

    this.setValidationDone(true);
    this.setIsValidNumber(valid);
    return valid;
  }

  public handleQueryParamMsisdn(): void {
    const msisdn = this.routeStore.queryParam('msisdn');
    if (msisdn) {
      this.setMobileNumber(msisdn);
      this.validateMobileNumber();
    }
  }

  private setValidationDone(isDone: boolean) {
    this.validationDone = isDone;
  }

  get validMobileNumber(): boolean {
    return this.prepaidInfo !== null && this.prepaidInfo.brand !== null && this.prepaidInfo.brand !== PrepaidBrand.UNKNOWN;
  }

  get numberFieldInvalid(): boolean {
    return !this.validNumber && this.validationDone;
  }

  get detaineeNumber(): boolean {
    return this.validMobileNumber && this.prepaidInfo.brand === PrepaidBrand.PRION;
  }

  get isPrepaidInfoLoading(): boolean {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.prepaidInfoStatus);
  }

  private setPrepaidInfo(val: IPrepaidInfo) {
    this.prepaidInfo = val;
  }

  private setIsValidNumber(isValid: boolean) {
    this.validNumber = isValid;
  }
}

export default MobileNumberStore;
